import { Link } from 'react-router-dom'
import { getTierByCi, TIERS } from '../lib/press'
import RankBadge from './RankBadge'

// ── ProfileCard ────────────────────────────────────────────────
// Full theme-aware profile card. Used on dashboard + writer profiles.
// theme prop = press_theme string from DB
// size = 'dashboard' | 'profile' (public writer page)

export default function ProfileCard({
  displayName, role, bio, columnInches = 0, isVoice = false, isFounding = false,
  pressTheme = 'default', size = 'dashboard',
  writerId, postCount = 0,
  children, // extra actions (buttons etc)
}) {
  const tier = getTierByCi(columnInches, isVoice)
  const T = THEMES[pressTheme] || THEMES.default
  const isLarge = size === 'profile'

  return (
    <>
      <style>{`
        @keyframes pc-pulse {
          0%,100% { box-shadow: 0 0 0 0 ${T.glowColor || 'transparent'}; }
          50%      { box-shadow: 0 0 18px 4px ${T.glowColor || 'transparent'}; }
        }
        @keyframes pc-ribbon {
          0%   { border-color: #C87A7A; }
          25%  { border-color: #7A9CB8; }
          50%  { border-color: #C8401A; }
          75%  { border-color: #C8A83A; }
          100% { border-color: #C87A7A; }
        }
        @keyframes pc-shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pc-scanline {
          0%   { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        @keyframes pc-flicker {
          0%,19%,21%,23%,25%,54%,56%,100% { opacity: 1; }
          20%,24%,55% { opacity: 0.4; }
        }
        .pc-voice-stamp {
          animation: pc-flicker 6s linear infinite;
        }
      `}</style>

      <div style={{
        ...T.card,
        position: 'relative',
        overflow: 'hidden',
        marginBottom: '2rem',
        animation: T.animated ? `${T.animation} ${T.animDuration || '3s'} ease-in-out infinite` : 'none',
      }}>

        {/* ── Background layer (decorative) ── */}
        {T.bgLayer && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, ...T.bgLayer }} />}

        {/* ── Top accent bar ── */}
        {T.topBar && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            height: T.topBarHeight || 4,
            background: T.topBar,
            zIndex: 1,
          }} />
        )}

        {/* ── Left accent bar ── */}
        {T.leftBar && (
          <div style={{
            position: 'absolute', top: 0, left: 0, bottom: 0,
            width: 5,
            background: T.leftBar,
            zIndex: 1,
          }} />
        )}

        {/* ── Watermark ── */}
        {T.watermark && (
          <div style={{
            position: 'absolute',
            right: -20, bottom: -20,
            fontFamily: T.watermarkFont || "'Space Grotesk', sans-serif",
            fontSize: T.watermarkSize || '8rem',
            fontWeight: 700,
            color: T.watermarkColor || 'rgba(0,0,0,0.04)',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            lineHeight: 1,
          }}>
            {T.watermark}
          </div>
        )}

        {/* ── Main content ── */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          padding: isLarge ? '3rem' : '1.75rem 2rem',
          paddingLeft: T.leftBar ? (isLarge ? '3.5rem' : '2.5rem') : undefined,
          paddingTop: T.topBar ? (isLarge ? '3.5rem' : '2.25rem') : undefined,
          paddingBottom: isVoice ? (isLarge ? '4rem' : '3rem') : undefined,
        }}>

          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.25rem', flexWrap: 'wrap' }}>

            {/* Avatar */}
            <div style={{
              width: isLarge ? 72 : 52,
              height: isLarge ? 72 : 52,
              borderRadius: T.avatarRound ? '50%' : 4,
              background: T.avatarBg || tier.color,
              border: T.avatarBorder || 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: isLarge ? '1.8rem' : '1.2rem',
              fontWeight: 700,
              color: T.avatarText || '#fff',
              fontFamily: T.avatarFont || "'Space Grotesk', sans-serif",
              flexShrink: 0,
              position: 'relative',
              overflow: 'hidden',
            }}>
              {displayName?.[0]?.toUpperCase()}
              {/* Avatar shimmer for high tiers */}
              {T.avatarShimmer && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                  backgroundSize: '200% 100%',
                  animation: 'pc-shimmer 2s linear infinite',
                }} />
              )}
            </div>

            {/* Name + rank + role */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                <span style={{
                  fontFamily: T.nameFont || "'Space Grotesk', sans-serif",
                  fontSize: isLarge ? T.nameSizeLg || '2rem' : T.nameSize || '1.3rem',
                  fontWeight: 700,
                  color: T.nameColor || 'var(--ink)',
                  letterSpacing: T.nameSpacing || '-0.02em',
                  lineHeight: 1.1,
                  ...(T.nameExtra || {}),
                }}>
                  {displayName}
                </span>
                <RankBadge columnInches={columnInches} isVoice={isVoice} size={isLarge ? 'md' : 'sm'} />
              </div>

              {/* Tier title */}
              <div style={{
                fontFamily: "'Courier New', monospace",
                fontSize: isLarge ? '0.72rem' : '0.62rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: tier.color,
                marginBottom: isFounding ? '0.5rem' : (bio ? '0.6rem' : 0),
              }}>
                {tier.title}{isVoice ? ' — Voice of The Margins' : ''}
              </div>

              {/* Founding member designation — sits directly beneath the rank */}
              {isFounding && (
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  background: 'var(--tier-voice)',
                  padding: '3px 9px',
                  borderRadius: 3,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: isLarge ? '0.5rem' : '0.44rem',
                  letterSpacing: '0.18em',
                  color: '#fff',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  marginBottom: bio ? '0.6rem' : 0,
                }}>
                  ◆ Founding Member
                </div>
              )}

              {bio && (
                <p style={{
                  fontSize: isLarge ? '0.95rem' : '0.875rem',
                  lineHeight: 1.7,
                  color: T.bioColor || 'var(--muted)',
                  fontFamily: T.bioFont,
                  maxWidth: '55ch',
                }}>
                  {bio}
                </p>
              )}
            </div>

            {/* Stats + actions (right side) */}
            {(postCount > 0 || children) && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.75rem', flexShrink: 0 }}>
                {postCount > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '2rem', fontWeight: 700,
                      color: tier.color, lineHeight: 1,
                    }}>
                      {postCount}
                    </div>
                    <div style={{
                      fontFamily: "'Courier New', monospace",
                      fontSize: '0.55rem', letterSpacing: '0.12em',
                      textTransform: 'uppercase', color: T.statLabelColor || 'var(--muted)',
                    }}>
                      Published
                    </div>
                  </div>
                )}
                {children}
              </div>
            )}
          </div>

          {/* CI progress bar (dashboard only) */}
          {size === 'dashboard' && (
            <div style={{ marginTop: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                <span style={{
                  fontFamily: "'Courier New', monospace",
                  fontSize: '0.55rem', color: T.progressLabelColor || 'var(--muted)',
                  letterSpacing: '0.06em',
                }}>
                  {columnInches} Column Inches
                </span>
                {TIERS[tier.rank] && TIERS[tier.rank].ci !== Infinity && (
                  <span style={{
                    fontFamily: "'Courier New', monospace",
                    fontSize: '0.55rem', color: tier.color,
                    letterSpacing: '0.06em',
                  }}>
                    {TIERS[tier.rank].ci - columnInches} CI to {TIERS[tier.rank].title}
                  </span>
                )}
              </div>
              <div style={{
                height: 6,
                background: T.progressBg || 'var(--rule)',
                overflow: 'hidden',
                position: 'relative',
              }}>
                {/* Column lines */}
                {Array(20).fill(null).map((_, i) => (
                  <div key={i} style={{
                    position: 'absolute',
                    left: `${i * 5}%`, top: 0, bottom: 0,
                    width: 1,
                    background: T.progressLines || 'rgba(255,255,255,0.1)',
                    zIndex: 1,
                  }} />
                ))}
                <div style={{
                  height: '100%',
                  width: `${TIERS[tier.rank] && TIERS[tier.rank].ci !== Infinity
                    ? Math.min(100, Math.round((columnInches - tier.ci) / (TIERS[tier.rank].ci - tier.ci) * 100))
                    : 100}%`,
                  background: T.progressFill || tier.color,
                  transition: 'width 0.8s cubic-bezier(0.16,1,0.3,1)',
                  boxShadow: T.progressGlow ? `0 0 8px ${tier.color}` : 'none',
                }} />
              </div>
            </div>
          )}

          {/* Voice special stamp */}
          {isVoice && (
            <div className="pc-voice-stamp" style={{
              position: 'absolute',
              bottom: isLarge ? 28 : 20,
              right: isLarge ? 32 : 20,
              border: '2px solid #C8401A',
              padding: '4px 10px',
              fontFamily: "'Courier New', monospace",
              fontSize: '0.5rem',
              letterSpacing: '0.16em',
              color: '#C8401A',
              textTransform: 'uppercase',
              transform: 'rotate(-3deg)',
              zIndex: 3,
            }}>
              ◆ Voice of The Margins
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ══════════════════════════════════════════════════════════════
// THEME DEFINITIONS — each one is a distinct visual identity
// ══════════════════════════════════════════════════════════════

const THEMES = {

  default: {
    card: {
      background: 'var(--paper)',
      border: '1px solid var(--rule)',
    },
    nameColor: 'var(--ink)',
    bioColor: 'var(--muted)',
    avatarBg: 'var(--accent)',
    avatarText: '#fff',
    avatarRound: true,
    progressBg: 'var(--rule)',
    progressLabelColor: 'var(--muted)',
    statLabelColor: 'var(--muted)',
  },

  // Tier 2 — Stringer: dark ink background
  ink: {
    card: {
      background: '#0F0E0C',
      border: '1px solid #2C2820',
    },
    topBar: '#2C2820',
    leftBar: '#7A9CB8',
    nameColor: '#EDE8DF',
    bioColor: '#7A7268',
    avatarRound: true,
    avatarBg: '#1A1714',
    avatarBorder: '2px solid #7A9CB8',
    avatarText: '#7A9CB8',
    progressBg: '#1A1714',
    progressLines: 'rgba(255,255,255,0.05)',
    progressLabelColor: '#4A4540',
    statLabelColor: '#4A4540',
    watermark: '✎',
    watermarkColor: 'rgba(122,156,184,0.08)',
    watermarkSize: '9rem',
  },

  // Tier 3 — Correspondent: ruled paper texture
  ruled: {
    card: {
      background: '#FAFAF5',
      border: '2px solid #7AB87A',
      backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, rgba(122,184,122,0.12) 27px, rgba(122,184,122,0.12) 28px)',
    },
    leftBar: '#7AB87A',
    nameColor: '#1A3A1A',
    bioColor: '#4A6A4A',
    avatarRound: false,
    avatarBg: '#7AB87A',
    progressFill: '#7AB87A',
    progressBg: 'rgba(122,184,122,0.15)',
    watermark: '◎',
    watermarkColor: 'rgba(122,184,122,0.1)',
  },

  // Tier 4 — Staff Writer: rust theme
  rust: {
    card: {
      background: '#0A0908',
      border: '2px solid #C8401A',
    },
    topBar: '#C8401A',
    topBarHeight: 3,
    leftBar: '#C8401A',
    nameColor: '#F5F0E8',
    nameExtra: { textShadow: '0 0 20px rgba(200,64,26,0.3)' },
    bioColor: '#7A7268',
    avatarRound: true,
    avatarBg: '#C8401A',
    avatarShimmer: true,
    progressFill: '#C8401A',
    progressBg: '#1A1714',
    progressGlow: true,
    watermark: '◉',
    watermarkColor: 'rgba(200,64,26,0.07)',
    watermarkSize: '10rem',
  },

  // Tier 5 — Beat Reporter: animated pulse border
  beat: {
    card: {
      background: '#0C0A08',
      border: '2px solid #C87A3A',
    },
    animated: true,
    animation: 'pc-pulse',
    animDuration: '2.5s',
    glowColor: 'rgba(200,122,58,0.3)',
    topBar: 'linear-gradient(90deg, #C87A3A, #E8A060, #C87A3A)',
    topBarHeight: 4,
    nameColor: '#F0E8D8',
    nameSpacing: '0.02em',
    bioColor: '#8A7560',
    avatarRound: true,
    avatarBg: '#C87A3A',
    progressFill: '#C87A3A',
    progressBg: '#1A1410',
    progressGlow: true,
    watermark: '◈',
    watermarkColor: 'rgba(200,122,58,0.08)',
  },

  // Tier 6 — Feature Writer: broadsheet newspaper
  broadsheet: {
    card: {
      background: '#FFFFF8',
      border: '1px solid #1A1A1A',
      borderTop: '4px solid #1A1A1A',
      boxShadow: '4px 4px 0 #1A1A1A',
    },
    nameFont: "'Space Grotesk', sans-serif",
    nameSize: '1.5rem',
    nameSizeLg: '2.2rem',
    nameColor: '#0A0908',
    nameSpacing: '-0.03em',
    bioColor: '#3A3530',
    avatarRound: false,
    avatarBg: '#0A0908',
    avatarText: '#FFFFF8',
    avatarFont: "'Space Grotesk', sans-serif",
    progressBg: '#E8E8E0',
    progressFill: '#0A0908',
    progressLines: 'rgba(0,0,0,0.1)',
    watermark: '❦',
    watermarkColor: 'rgba(0,0,0,0.04)',
    watermarkFont: 'serif',
    watermarkSize: '9rem',
  },

  // Tier 7 — Columnist: dark grid pressroom
  pressroom: {
    card: {
      background: '#080706',
      border: '1px solid #C8A83A',
    },
    bgLayer: {
      backgroundImage: 'radial-gradient(rgba(200,168,58,0.06) 1px, transparent 1px)',
      backgroundSize: '18px 18px',
    },
    topBar: '#C8A83A',
    topBarHeight: 3,
    nameColor: '#C8A83A',
    nameFont: "'Courier New', monospace",
    nameSpacing: '0.04em',
    nameExtra: { textTransform: 'uppercase' },
    bioColor: '#6A6040',
    avatarRound: false,
    avatarBg: '#C8A83A',
    avatarText: '#080706',
    avatarFont: "'Courier New', monospace",
    progressFill: '#C8A83A',
    progressBg: '#141210',
    progressGlow: true,
    watermark: '↻',
    watermarkColor: 'rgba(200,168,58,0.06)',
    watermarkSize: '10rem',
  },

  // Tier 8 — Senior Correspondent: animated typewriter ribbon
  typewriter: {
    card: {
      background: '#1A1410',
      border: '2px solid #C87A7A',
    },
    animated: true,
    animation: 'pc-ribbon',
    animDuration: '4s',
    topBar: '#C87A7A',
    topBarHeight: 5,
    nameFont: "'Courier New', monospace",
    nameSpacing: '0.06em',
    nameExtra: { textTransform: 'uppercase', fontWeight: 700 },
    nameColor: '#F0E0E0',
    bioColor: '#8A6060',
    avatarRound: false,
    avatarBg: '#2A1A1A',
    avatarBorder: '2px solid #C87A7A',
    avatarText: '#C87A7A',
    progressFill: '#C87A7A',
    progressBg: '#0A0808',
    progressGlow: true,
    bgLayer: {
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(200,122,122,0.04) 19px, rgba(200,122,122,0.04) 20px)',
    },
    watermark: '◆',
    watermarkColor: 'rgba(200,122,122,0.06)',
  },

  // Tier 9 — Contributing Editor: aged sepia press credential
  aged: {
    card: {
      background: '#F2E8D0',
      border: '2px solid #8B6914',
      boxShadow: 'inset 0 0 60px rgba(139,105,20,0.08)',
    },
    bgLayer: {
      background: 'radial-gradient(ellipse at center, transparent 40%, rgba(139,105,20,0.06) 100%)',
    },
    topBar: '#8B6914',
    topBarHeight: 4,
    leftBar: '#8B6914',
    nameFont: "'Space Grotesk', sans-serif",
    nameColor: '#3A2800',
    nameSpacing: '-0.01em',
    bioColor: '#6A5020',
    avatarRound: false,
    avatarBg: '#8B6914',
    avatarText: '#F2E8D0',
    progressFill: '#8B6914',
    progressBg: 'rgba(139,105,20,0.15)',
    watermark: '✦',
    watermarkColor: 'rgba(139,105,20,0.08)',
    watermarkFont: 'serif',
  },

  // Tier 10 — Senior Editor: wire service terminal
  wire: {
    card: {
      background: '#020C0C',
      border: '1px solid #7AB8B8',
      boxShadow: '0 0 30px rgba(122,184,184,0.08), inset 0 0 30px rgba(0,0,0,0.5)',
    },
    bgLayer: {
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(122,184,184,0.02) 2px, rgba(122,184,184,0.02) 4px)',
    },
    topBar: '#7AB8B8',
    topBarHeight: 2,
    nameFont: "'Courier New', monospace",
    nameSpacing: '0.1em',
    nameExtra: { textTransform: 'uppercase' },
    nameColor: '#7AB8B8',
    bioColor: '#4A8080',
    avatarRound: false,
    avatarBg: '#051414',
    avatarBorder: '1px solid #7AB8B8',
    avatarText: '#7AB8B8',
    progressFill: '#7AB8B8',
    progressBg: '#051414',
    progressGlow: true,
    progressLines: 'rgba(122,184,184,0.1)',
    watermark: '◐',
    watermarkColor: 'rgba(122,184,184,0.06)',
  },

  // Tier 11 — Editor at Large: GOLD MASTHEAD
  masthead: {
    card: {
      background: 'linear-gradient(135deg, #0A0800 0%, #140F00 50%, #0A0800 100%)',
      border: '2px solid #D4AF37',
      boxShadow: '0 0 40px rgba(212,175,55,0.15), inset 0 0 40px rgba(0,0,0,0.5)',
    },
    animated: true,
    animation: 'pc-pulse',
    animDuration: '3s',
    glowColor: 'rgba(212,175,55,0.25)',
    bgLayer: {
      backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(212,175,55,0.04) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(212,175,55,0.04) 0%, transparent 60%)',
    },
    topBar: 'linear-gradient(90deg, transparent, #D4AF37, #FFE878, #D4AF37, transparent)',
    topBarHeight: 3,
    nameFont: "'Space Grotesk', sans-serif",
    nameSize: '1.6rem',
    nameSizeLg: '2.5rem',
    nameSpacing: '-0.02em',
    nameColor: '#D4AF37',
    nameExtra: { textShadow: '0 0 30px rgba(212,175,55,0.5)' },
    bioColor: '#8A7830',
    avatarRound: true,
    avatarBg: 'linear-gradient(135deg, #D4AF37, #FFE878, #B8960C)',
    avatarText: '#0A0800',
    avatarShimmer: true,
    progressFill: 'linear-gradient(90deg, #B8960C, #D4AF37, #FFE878)',
    progressBg: '#1A1400',
    progressGlow: true,
    progressLines: 'rgba(212,175,55,0.1)',
    statLabelColor: '#8A7830',
    watermark: '◆',
    watermarkColor: 'rgba(212,175,55,0.06)',
    watermarkSize: '12rem',
  },

  // Tier 12 — The Voice: FULL CRIMSON LEGENDARY
  voice: {
    card: {
      background: 'linear-gradient(135deg, #1A0000 0%, #0A0000 50%, #1A0000 100%)',
      border: '2px solid #C8401A',
      boxShadow: '0 0 60px rgba(200,64,26,0.2), inset 0 0 60px rgba(0,0,0,0.6)',
    },
    animated: true,
    animation: 'pc-pulse',
    animDuration: '2s',
    glowColor: 'rgba(200,64,26,0.4)',
    bgLayer: {
      backgroundImage: 'radial-gradient(ellipse at center, rgba(200,64,26,0.06) 0%, transparent 70%)',
    },
    topBar: 'linear-gradient(90deg, transparent, #C8401A, #E8653A, #C8401A, transparent)',
    topBarHeight: 4,
    nameFont: "'Space Grotesk', sans-serif",
    nameSize: '1.7rem',
    nameSizeLg: '2.8rem',
    nameSpacing: '-0.02em',
    nameColor: '#E8653A',
    nameExtra: { textShadow: '0 0 40px rgba(200,64,26,0.6)' },
    bioColor: '#8A4030',
    avatarRound: true,
    avatarBg: '#C8401A',
    avatarText: '#fff',
    avatarShimmer: true,
    progressFill: 'linear-gradient(90deg, #8B1A00, #C8401A, #E8653A)',
    progressBg: '#1A0000',
    progressGlow: true,
    progressLines: 'rgba(200,64,26,0.15)',
    watermark: '◆',
    watermarkColor: 'rgba(200,64,26,0.08)',
    watermarkSize: '12rem',
  },
}
